import { ChangeEventHandler, useMemo, useState } from 'react';

import { useUpdateVenueMutation } from '@admin/mutations/VenuesMutations';

import { useAppSettingsQuery } from '@queries/AppQueries';
import {
  FeedEvent,
  UserEventRsvp,
  useCurrentUserEventRsvpsQuery,
  useEventQuery,
} from '@queries/EventQueries';
import { Series, useSeriesByTagId, useSeriesEntertainersQuery } from '@queries/TagQueries';
import { SuperAdminVenue, useCityVenuesQuery } from '@queries/VenueQueries';

import { useRsvpToEventMutation, useUpdateEventMutation } from '@mutations/EventMutations';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import { Toggle } from '@components/form/Toggle';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { MultiSelectField } from '@screens/entertainer/components/CitiesField';

interface WrappedEventItemProps {
  event: any;
  options: { value: string; label: string }[];
  onUpdate: () => void;
}

const WrappedEventItem = ({ event, options, onUpdate }: WrappedEventItemProps) => {
  const [isFlyerVisible, setIsFlyerVisible] = useState(false);
  const [entertainerIds, setEntertainerIds] = useState<string[]>(
    event.entertainers?.map(({ _id }) => _id) ?? [],
  );

  const isDirty = useMemo(() => {
    const currentEntertainerIds = event.entertainers?.map(({ _id }) => _id) ?? [];
    return JSON.stringify(entertainerIds.sort()) !== JSON.stringify(currentEntertainerIds.sort());
  }, [entertainerIds, event.entertainers]);

  const { updateEventAsync } = useUpdateEventMutation({ eventId: event._id });

  const handleSave = async () => {
    await updateEventAsync({
      entertainerIds,
    });
    onUpdate();
  };

  return (
    <div>
      {isFlyerVisible && (
        <div>
          <img src={event.img} style={{ width: '100%' }} />
        </div>
      )}
      <div className="d-flex gap-2 align-items-center justify-content-between">
        <div>
          <div>
            <strong>{event.title}</strong>
          </div>
          <div style={{ fontSize: '.8rem', opacity: 0.8 }}>
            <div>{event.start}</div>
            <div>{event.venue?.title}</div>
          </div>
        </div>
        <div className="text-align-right">
          <div>{`${event.entertainers?.length ?? 0} total`}</div>
          <div onClick={() => setIsFlyerVisible(true)}>
            <u>View Flyer</u>
          </div>
        </div>
      </div>
      <div className="mt-1">
        <MultiSelectField
          name="entertainerIds"
          allowSearch
          defaultValue={entertainerIds}
          options={options.sort((a, b) => a.label.localeCompare(b.label))}
          onChange={setEntertainerIds}
        />
        {isDirty && (
          <div className="mt-2">
            <Button onClick={handleSave}>Save</Button>
          </div>
        )}
      </div>
    </div>
  );
};

interface EventItemProps extends Omit<WrappedEventItemProps, 'event'> {
  event: FeedEvent;
}

const EventItem = ({ event: feedEvent, options, onUpdate }: EventItemProps) => {
  const { event } = useEventQuery(feedEvent._id);

  if (event) {
    return <WrappedEventItem event={event} options={options} onUpdate={onUpdate} />;
  }

  return <></>;
};

export const ExistingEventsEntertainersList = ({
  tagId,
  events,
}: {
  tagId: string;
  events: FeedEvent[];
}) => {
  const { entertainers } = useSeriesEntertainersQuery(tagId);

  return (
    <>
      <div className="d-grid gap-3">
        {events.map((event) => (
          <EventItem
            key={event._id}
            event={event}
            options={entertainers.map(({ _id, title }) => ({
              value: _id,
              label: title ?? _id,
            }))}
            onUpdate={() => {}}
          />
        ))}
      </div>
    </>
  );
};
